import { z } from "zod";

export const userStatusSchema = z.enum(["active", "disabled"]);
export type UserStatus = z.infer<typeof userStatusSchema>;

/** Admin lists may receive a private avatar reference that is not a public HTTPS URL. */
export const adminAvatarUrlSchema = z.preprocess(
  (value) => (value === "" || value == null ? null : value),
  z.string().max(2048).nullable(),
);

export const userRecordSchema = z.object({
  id: z.string().uuid(),
  publicId: z.number().int().min(1001),
  avatarUrl: adminAvatarUrlSchema,
  displayName: z.string().trim().min(1).max(64),
  maskedPhone: z.string().max(32),
  maskedUnionId: z.string().max(64).nullable(),
  maskedWechatOpenIds: z.array(z.string().max(64)).optional(),
  maskedEmail: z.string().max(128).nullable(),
  region: z.string().max(64).nullable(),
  locale: z.string().max(16).nullable(),
  tags: z.preprocess((value) => value ?? [], z.array(z.string().min(1).max(32)).max(20)),
  appProfiles: z.record(z.string(), z.string()).default({}),
  appSelfProfiles: z.array(z.object({
    appId: z.string().min(1).max(128),
    appName: z.string().min(1).max(64),
    values: z.record(z.string(), z.json()).default({}),
  }).strict()).default([]),
  verificationStatus: z.enum(["verified", "partial", "unverified"]),
  riskLevel: z.enum(["low", "medium", "high"]),
  loginCount: z.number().int().nonnegative(),
  deviceCount: z.number().int().nonnegative(),
  lastActiveAt: z.string().datetime().nullable(),
  registrationSource: z.string().max(64),
  lastLoginDevice: z.string().max(128).nullable(),
  lastLoginIp: z.string().max(64).nullable(),
  consentAt: z.string().datetime().nullable(),
  gender: z.enum(["male", "female", "unknown"]).optional(),
  ageGroup: z.enum(["18-24", "25-34", "35-44", "45+", "unknown"]).optional(),
  deviceType: z.enum(["ios", "android", "desktop", "other"]).optional(),
  status: userStatusSchema,
  deletionCompleted: z.boolean().optional(),
  sourceApps: z.array(z.string().min(1)),
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
});
export type UserRecord = z.infer<typeof userRecordSchema>;

export const userListQuerySchema = z.object({
  registeredFrom: z.iso.date().optional(),
  registeredTo: z.iso.date().optional(),
  riskLevel: z.enum(["all", "low", "medium", "high"]).optional(),
  excludeMerged: z.boolean().optional(),
  keyword: z.string().trim().max(100).default(""),
  status: z.union([userStatusSchema, z.literal("all")]).default("all"),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).refine(input => !input.registeredFrom || !input.registeredTo || input.registeredFrom <= input.registeredTo, {
  path: ["registeredTo"], message: "注册截止日期不能早于起始日期",
});
export type UserListQuery = z.input<typeof userListQuerySchema>;

export const adminUserAvatarSchema = z.object({
  contentType: z.literal("image/png"),
  base64: z.string().min(1).max(360000),
}).strict();
export type AdminUserAvatar = z.infer<typeof adminUserAvatarSchema>;

export const userListResultSchema = z.object({ items: z.array(userRecordSchema), total: z.number().int().nonnegative(), activeTotal: z.number().int().nonnegative() });
export type UserListResult = z.infer<typeof userListResultSchema>;

export const updateUserInputSchema = z.object({ status: userStatusSchema });
export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;

export const accountMergeDecisionSchema = z.enum(["auto_bind", "manual_review"]);
export type AccountMergeDecision = z.infer<typeof accountMergeDecisionSchema>;

export const mergeCaseStatusSchema = z.enum(["pending", "approved", "rejected"]);
export const mergeAccountSchema = z.object({
  id: z.string().min(1).max(128),
  publicId: z.number().int().min(1001),
  displayName: z.string().min(1).max(64),
  avatarUrl: adminAvatarUrlSchema,
  maskedPhone: z.string().max(32),
  maskedUnionId: z.string().max(64).nullable(),
  maskedWechatOpenIds: z.array(z.string().max(64)).optional(),
  sourceApps: z.array(z.string().min(1).max(64)),
  createdAt: z.string().datetime(),
  orderCount: z.number().int().nonnegative(),
  membership: z.string().max(64),
});
export type MergeAccount = z.infer<typeof mergeAccountSchema>;

export const mergeCaseSchema = z.object({
  id: z.string().min(1).max(128),
  reason: z.string().min(1).max(300),
  detectedAt: z.string().datetime(),
  risk: z.enum(["medium", "high"]),
  status: mergeCaseStatusSchema,
  version: z.number().int().positive(),
  primaryAccountId: z.string().max(128).nullable(),
  reviewedAt: z.string().datetime().nullable(),
  reviewedBy: z.string().max(128).nullable(),
  reviewReason: z.string().max(500).nullable(),
  accounts: z.tuple([mergeAccountSchema, mergeAccountSchema]),
});
export type MergeCase = z.infer<typeof mergeCaseSchema>;

export const mergeCaseListInputSchema = z.object({
  status: z.enum(["pending", "processed"]).default("pending"),
  keyword: z.string().trim().max(100).default(""),
  page: z.number().int().min(1).max(10_000).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});
export type MergeCaseListInput = z.infer<typeof mergeCaseListInputSchema>;
export const mergeCaseListResultSchema = z.object({ items: z.array(mergeCaseSchema), total: z.number().int().nonnegative() });
export type MergeCaseListResult = z.infer<typeof mergeCaseListResultSchema>;

export const reviewMergeCaseInputSchema = z.object({
  caseId: z.string().min(1).max(128),
  version: z.number().int().positive(),
  decision: z.enum(["approve", "reject"]),
  primaryAccountId: z.string().min(1).max(128).optional(),
  reason: z.string().trim().min(5).max(500),
}).superRefine((value, context) => {
  if (value.decision === "approve" && !value.primaryAccountId) context.addIssue({ code: "custom", path: ["primaryAccountId"], message: "确认合并时必须选择主账号" });
});
export type ReviewMergeCaseInput = z.infer<typeof reviewMergeCaseInputSchema>;

export const securityEventTypeSchema = z.enum(["login_success", "login_failed", "suspicious", "blocked"]);
export const securityEventSchema = z.object({
  id: z.string().min(1).max(128),
  appId: z.string().max(128).nullable().optional(),
  appName: z.string().max(200).nullable().optional(),
  userId: z.string().uuid().nullable(),
  userName: z.string().max(64),
  eventType: securityEventTypeSchema,
  riskLevel: z.enum(["low", "medium", "high"]),
  ipMasked: z.string().max(64),
  device: z.string().max(128),
  location: z.string().max(64),
  reason: z.string().max(300),
  occurredAt: z.string().datetime(),
});
export type SecurityEvent = z.infer<typeof securityEventSchema>;
export const securityEventListInputSchema = z.object({
  eventType: z.union([securityEventTypeSchema, z.literal("all")]).default("all"),
  appId: z.string().trim().min(1).max(128).optional(),
  keyword: z.string().trim().max(100).default(""),
  page: z.number().int().min(1).max(10_000).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});
export type SecurityEventListInput = z.input<typeof securityEventListInputSchema>;
export const securityEventListResultSchema = z.object({ items: z.array(securityEventSchema), total: z.number().int().nonnegative() });
export type SecurityEventListResult = z.infer<typeof securityEventListResultSchema>;

export const userTagRuleTypeSchema = z.enum(["manual", "new_user_days", "min_login_count", "min_app_count"]);
export type UserTagRuleType = z.infer<typeof userTagRuleTypeSchema>;
export const userTagSummarySchema = z.object({
  id: z.string().min(1).max(128).nullable(),
  name: z.string().min(1).max(32),
  count: z.number().int().nonnegative(),
  color: z.string().min(1).max(32),
  description: z.string().max(200),
  ruleType: userTagRuleTypeSchema,
  ruleValue: z.number().int().positive().nullable(),
  enabled: z.boolean(),
  version: z.number().int().positive(),
});
export type UserTagSummary = z.infer<typeof userTagSummarySchema>;
export const userTagListInputSchema = z.object({ keyword: z.string().trim().max(100).default(""), page: z.number().int().min(1).max(10_000).default(1), pageSize: z.number().int().min(1).max(100).default(100) });
export type UserTagListInput = z.input<typeof userTagListInputSchema>;
export const userTagListResultSchema = z.object({ items: z.array(userTagSummarySchema), total: z.number().int().nonnegative(), coveredUsers: z.number().int().nonnegative(), totalUsers: z.number().int().nonnegative() });
export type UserTagListResult = z.infer<typeof userTagListResultSchema>;
export const saveUserTagInputSchema = z.object({
  id: z.string().min(1).max(128).optional(),
  version: z.number().int().positive().optional(),
  name: z.string().trim().min(1).max(32),
  color: z.string().min(1).max(32),
  description: z.string().trim().max(200),
  ruleType: userTagRuleTypeSchema,
  ruleValue: z.number().int().positive().max(1_000_000).nullable(),
  enabled: z.boolean(),
}).superRefine((value, context) => {
  if (Boolean(value.id) !== Boolean(value.version)) context.addIssue({ code: "custom", path: ["version"], message: "编辑标签时必须提供当前版本" });
  if (value.ruleType === "manual" && value.ruleValue !== null) context.addIssue({ code: "custom", path: ["ruleValue"], message: "手动标签不需要规则值" });
  if (value.ruleType !== "manual" && value.ruleValue === null) context.addIssue({ code: "custom", path: ["ruleValue"], message: "自动标签必须提供规则值" });
  if (value.ruleType === "new_user_days" && (value.ruleValue ?? 0) > 365) context.addIssue({ code: "custom", path: ["ruleValue"], message: "注册天数不能超过 365" });
  if (value.ruleType === "min_app_count" && (value.ruleValue ?? 0) > 100) context.addIssue({ code: "custom", path: ["ruleValue"], message: "关联应用数不能超过 100" });
});
export type SaveUserTagInput = z.infer<typeof saveUserTagInputSchema>;
// Admins enter the displayed publicId; UUID remains accepted for existing callers.
export const userTagUserReferenceSchema = z.union([z.string().uuid(), z.string().regex(/^[1-9]\d*$/).refine(value => Number.isSafeInteger(Number(value)) && Number(value) >= 1001, "请输入有效的数字用户编号")]);
export const mutateUserTagAssignmentsInputSchema = z.object({
  tagId: z.string().min(1).max(128),
  userIds: z.array(userTagUserReferenceSchema).min(1).max(500),
  operation: z.enum(["apply", "remove"]),
});
export type MutateUserTagAssignmentsInput = z.infer<typeof mutateUserTagAssignmentsInputSchema>;
export const mutateUserTagAssignmentsResultSchema = z.object({
  tagId: z.string(), operation: z.enum(["apply", "remove"]),
  succeeded: z.array(userTagUserReferenceSchema), failed: z.array(z.object({ userId: userTagUserReferenceSchema, reason: z.string() })),
});
export type MutateUserTagAssignmentsResult = z.infer<typeof mutateUserTagAssignmentsResultSchema>;
export const recallTouchInputSchema = z.object({ userIds: z.array(z.string().uuid()).min(1).max(500), channel: z.string().trim().min(1).max(64), results: z.record(z.string().uuid(), z.enum(["success","failed"])).default({}) }).superRefine((value, context) => {
  if (value.userIds.some(id => !value.results[id]) || Object.keys(value.results).some(id => !value.userIds.includes(id))) context.addIssue({ code: "custom", path: ["results"], message: "必须明确记录每个用户的实际触达结果" });
});
export type RecallTouchInput = z.infer<typeof recallTouchInputSchema>;
export const recallTouchResultSchema = z.object({ channel: z.string(), succeeded: z.array(z.string().uuid()), failed: z.array(z.object({ userId: z.string().uuid(), reason: z.string() })) });
export type RecallTouchResult = z.infer<typeof recallTouchResultSchema>;

export const deletionRequestStatusSchema = z.enum(["pending", "reviewing", "completed", "rejected", "withdrawn"]);
export const deletionBlockerSchema = z.enum(["MERGED_USER", "ALREADY_COMPLETED", "OPEN_ORDERS", "PENDING_REFUNDS_OR_DELIVERY", "ASSET_DEBT", "REMAINING_ASSETS"]);
export const deletionRequestSchema = z.object({
  userPublicId: z.number().int().positive().optional(),
  blockers: z.array(deletionBlockerSchema).optional(),
  id: z.string().min(1).max(128),
  userId: z.string().uuid(),
  userName: z.string().max(64),
  maskedPhone: z.string().max(32),
  createdAt: z.string().datetime(),
  status: deletionRequestStatusSchema,
  version: z.number().int().positive(),
  reason: z.string().max(500),
  reviewedAt: z.string().datetime().nullable(),
  reviewedBy: z.string().max(128).nullable(),
  reviewReason: z.string().max(500).nullable(),
});
export type DeletionRequest = z.infer<typeof deletionRequestSchema>;
export const deletionRequestListInputSchema = z.object({ status: z.union([deletionRequestStatusSchema, z.literal("all"), z.literal("open")]).default("all"), page: z.number().int().min(1).max(10_000).default(1), pageSize: z.number().int().min(1).max(100).default(100) });
export type DeletionRequestListInput = z.input<typeof deletionRequestListInputSchema>;
export const deletionRequestListResultSchema = z.object({ items: z.array(deletionRequestSchema), total: z.number().int().nonnegative() });
export type DeletionRequestListResult = z.infer<typeof deletionRequestListResultSchema>;
export const deletionReviewDecisionSchema = z.enum(["start_review", "complete", "reject"]);
export const reviewDeletionRequestInputSchema = z.object({
  requestId: z.string().min(1).max(128),
  version: z.number().int().positive(),
  decision: deletionReviewDecisionSchema,
  reason: z.string().trim().max(500).default(""),
}).superRefine((value, context) => {
  if (value.decision !== "start_review" && value.reason.length < 5) context.addIssue({ code: "custom", path: ["reason"], message: "请填写至少 5 个字符的审核依据" });
});
export type ReviewDeletionRequestInput = z.infer<typeof reviewDeletionRequestInputSchema>;


// User-facing closure is platform-wide; it does not promise erasure of records.
export const selfDeletionSubmitSchema = z.object({
  mutationId: z.uuid(), reason: z.string().trim().min(5).max(500),
  scope: z.literal("platform"), acknowledged: z.literal(true),
}).strict();
export const selfDeletionWithdrawSchema = z.object({
  mutationId: z.uuid(), requestId: z.string().min(1).max(128), version: z.number().int().positive(),
}).strict();
export const selfDeletionRequestSchema = z.object({
  id: z.string().min(1), status: deletionRequestStatusSchema, version: z.number().int().positive(),
  reason: z.string(), createdAt: z.string().datetime(), reviewedAt: z.string().datetime().nullable(),
  reviewReason: z.string().nullable(), canWithdraw: z.boolean(),
}).strict();
export const selfDeletionStateSchema = z.object({
  scope: z.literal("platform"), dataPolicy: z.literal("close_and_retain"),
  blockers: z.array(deletionBlockerSchema), request: selfDeletionRequestSchema.nullable(),
}).strict();
export const selfDeletionSubmitResultSchema = selfDeletionStateSchema.extend({
  receiptToken: z.string().min(20).max(256), receiptExpiresAt: z.string().datetime(),
}).strict();
export const selfDeletionReceiptInputSchema = z.object({ receiptToken: z.string().min(20).max(256) }).strict();
export const selfDeletionProgressSchema = z.object({
  requestId: z.string().min(1), status: deletionRequestStatusSchema, version: z.number().int().positive(),
  reviewedAt: z.string().datetime().nullable(),
}).strict();

// Financial follow-up for retained, disabled accounts. No debt write-off API.
export const closedSettlementStatusSchema = z.enum(['open', 'reviewing', 'reconciled']);
export const closedSettlementDebtSchema = z.object({
  poolId: z.uuid(), poolName: z.string(), kind: z.enum(['membership', 'points', 'item']),
  sandbox: z.boolean(), assetKey: z.string(), debt: z.number().int().positive().safe(), frozen: z.boolean(),
});
export const closedSettlementSchema = z.object({
  id: z.uuid(), userId: z.uuid(), userPublicId: z.number().int().positive(), userName: z.string(),
  status: closedSettlementStatusSchema, version: z.number().int().positive(), reviewer: z.string().nullable(),
  lastPurchaseId: z.uuid().nullable(), createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
  debts: z.array(closedSettlementDebtSchema), blockers: z.array(deletionBlockerSchema), reviewChanged: z.boolean(),
});
export type ClosedSettlement = z.infer<typeof closedSettlementSchema>;
export const closedSettlementListInputSchema = z.object({
  status: z.union([closedSettlementStatusSchema, z.literal('all')]).default('all'), userId: z.uuid().optional(),
  page: z.number().int().min(1).max(10000).default(1), pageSize: z.number().int().min(1).max(100).default(20),
}).strict();
export type ClosedSettlementListInput = z.input<typeof closedSettlementListInputSchema>;
export const closedSettlementListResultSchema = z.object({items: z.array(closedSettlementSchema), total: z.number().int().nonnegative()});
export type ClosedSettlementListResult = z.infer<typeof closedSettlementListResultSchema>;
export const reviewClosedSettlementInputSchema = z.object({
  caseId: z.uuid(), version: z.number().int().positive(), mutationId: z.uuid(),
  decision: z.enum(['start_review', 'note', 'reconcile']), reason: z.string().trim().min(5).max(500),
}).strict();
export type ReviewClosedSettlementInput = z.infer<typeof reviewClosedSettlementInputSchema>;
export const closedSettlementAuditInputSchema = z.object({caseId: z.uuid(), page: z.number().int().min(1).max(10000).default(1), pageSize: z.number().int().min(1).max(100).default(20)}).strict();
export type ClosedSettlementAuditInput = z.input<typeof closedSettlementAuditInputSchema>;
export const closedSettlementAuditResultSchema = z.object({items: z.array(z.object({
  id: z.uuid(), action: z.enum(['refund_observed','start_review','note','reconcile']), actor: z.string(), reason: z.string(),
  sourceKey: z.string().nullable(), createdAt: z.string().datetime(), snapshot: z.object({debts:z.array(closedSettlementDebtSchema), blockers:z.array(deletionBlockerSchema), refundFingerprint:z.string()}),
})), total: z.number().int().nonnegative()});
export type ClosedSettlementAuditResult = z.infer<typeof closedSettlementAuditResultSchema>;
